import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Container, Paper, Typography, Button, Box } from '@mui/material';
import { Timeline, TimelineItem, TimelineSeparator, TimelineConnector, TimelineContent, TimelineDot } from '@mui/lab';
import ReactMarkdown from 'react-markdown';
import { TaskData } from './TaskData';

interface MarkdownData {
  id: string;
  title: string;
  content: string;
}

type StepType = 'start' | 'normal' | 'finish';

interface Step {
  title: string;
  description: string;
  type: StepType;
}

const TaskDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [task, setTask] = useState<TaskData | null>(null);
  const [markdown, setMarkdown] = useState<MarkdownData | null>(null);
  const [steps, setSteps] = useState<Step[]>([]);
  const [checkpoints, setCheckpoints] = useState<{ step: string; timestamp: string }[]>([]);
  const [startedAt, setStartedAt] = useState<string | null>(null);
  const [finishedAt, setFinishedAt] = useState<string | null>(null);

  useEffect(() => {
    if (id) {
      const savedTasks = localStorage.getItem('task-list');
      if (savedTasks) {
        const taskList: TaskData[] = JSON.parse(savedTasks);
        const foundTask = taskList.find(t => t.id === id);
        if (foundTask) {
          setTask(foundTask);
          if (foundTask.startedAt) setStartedAt(foundTask.startedAt);
          if (foundTask.finishedAt) setFinishedAt(foundTask.finishedAt);
          if (foundTask.checkpoints) setCheckpoints(foundTask.checkpoints);
        }
      }
    }
  }, [id]);

  useEffect(() => {
    if (task?.markdownId) {
      const savedMarkdowns = localStorage.getItem('markdown-list');
      if (savedMarkdowns) {
        const markdownList: MarkdownData[] = JSON.parse(savedMarkdowns);
        const foundMarkdown = markdownList.find(m => m.id === task.markdownId);
        if (foundMarkdown) {
          setMarkdown(foundMarkdown);
          extractSteps(foundMarkdown.content);
        }
      }
    }
  }, [task?.markdownId]);

  const extractSteps = (content: string) => {
    const lines = content.split('\n');
    const normalSteps: Step[] = [];
    let currentStep: Step | null = null;
    lines.forEach((line) => {
      if (line.startsWith('##')) {
        if (currentStep) {
          normalSteps.push(currentStep);
        }
        currentStep = { title: line.replace(/^##\s*/, '').trim(), description: '', type: 'normal' };
      } else if (currentStep) {
        currentStep.description += line + '\n';
      }
    });
    if (currentStep) {
      normalSteps.push(currentStep);
    }

    const fullSteps: Step[] = [
      { title: '開始', description: 'タスクの開始', type: 'start' },
      ...normalSteps,
      { title: '完了', description: 'タスクの完了', type: 'finish' },
    ];
    setSteps(fullSteps);
  };

  const saveTask = (updatedTask: TaskData) => {
    const savedTasks = localStorage.getItem('task-list');
    if (savedTasks) {
      const taskList: TaskData[] = JSON.parse(savedTasks);
      const updatedList = taskList.map(t => t.id === updatedTask.id ? updatedTask : t);
      localStorage.setItem('task-list', JSON.stringify(updatedList));
    }
  };

  const handleStart = () => {
    if (!task) return;
    const now = new Date().toISOString();
    const updatedTask = { ...task, startedAt: now, status: '進行中' as const };
    setTask(updatedTask);
    setStartedAt(now);
    saveTask(updatedTask);
  };

  const handleCheckpoint = (step: string) => {
    if (!task) return;
    const now = new Date().toISOString();
    const newCheckpoints = [...(checkpoints || []), { step, timestamp: now }];
    const updatedTask = { ...task, checkpoints: newCheckpoints };
    setTask(updatedTask);
    setCheckpoints(newCheckpoints);
    saveTask(updatedTask);
  };

  const handleFinish = () => {
    if (!task) return;
    const now = new Date().toISOString();
    const updatedTask = { ...task, finishedAt: now, status: '完了' as const };
    setTask(updatedTask);
    setFinishedAt(now);
    saveTask(updatedTask);
    alert('タスクを完了しました');
  };

  const getCheckpointTime = (step: string) => {
    if (step === '開始' && startedAt) {
      return new Date(startedAt).toLocaleString();
    }
    if (step === '完了' && finishedAt) {
      return new Date(finishedAt).toLocaleString();
    }
    const checkpoint = checkpoints.find(cp => cp.step === step);
    return checkpoint ? new Date(checkpoint.timestamp).toLocaleString() : '';
  };

  const getElapsedTime = () => {
    if (!startedAt || !finishedAt) return '';
    const start = new Date(startedAt).getTime();
    const finish = new Date(finishedAt).getTime();
    const diffMs = finish - start;
    const minutes = Math.floor(diffMs / 60000);
    const seconds = Math.floor((diffMs % 60000) / 1000);
    return `${minutes}分 ${seconds}秒`;
  };

  const getCurrentStepIndex = () => {
    let index = 0;
    if (startedAt) index++;
    index += checkpoints.length;
    return index;
  };

  if (!task || !markdown) {
    return <Typography>読み込み中...</Typography>;
  }

  const isFinished = !!finishedAt;
  const currentStepIndex = getCurrentStepIndex();

  return (
    <Container maxWidth="md" sx={{ mt: 4, pb: 6 }}>
      {/* 詳細部分 */}
      <Paper elevation={2} sx={{ p: 4, mb: 4, borderRadius: 2 }}>
        <Typography variant="h4" gutterBottom>{markdown.title}</Typography>

        {steps.map((step, index) => (
          <Paper
            key={index}
            variant="outlined"
            sx={{
              p: 3,
              my: 2,
              borderRadius: 2,
              backgroundColor: index === currentStepIndex ? '#e3f2fd' : '#ffffff',
              transition: 'background-color 0.3s ease'
            }}
          >
            <Typography variant="h6" gutterBottom>{step.title}</Typography>
            {step.type === 'normal' && (
              <ReactMarkdown>{step.description.trim()}</ReactMarkdown>
            )}
            <Typography variant="body2" color="text.secondary" mt={1}>
              {getCheckpointTime(step.title)}
            </Typography>

            {!isFinished && (
              <Box mt={2}>
                {step.type === 'start' ? (
                  <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                    sx={{ borderRadius: 2 }}
                    onClick={handleStart}
                    disabled={index !== currentStepIndex}
                  >
                    開始
                  </Button>
                ) : step.type === 'finish' ? (
                  <Button
                    variant="contained"
                    color="success"
                    fullWidth
                    sx={{ borderRadius: 2 }}
                    onClick={handleFinish}
                    disabled={index !== currentStepIndex}
                  >
                    完了
                  </Button>
                ) : (
                  <Button
                    variant="outlined"
                    fullWidth
                    sx={{ borderRadius: 2 }}
                    onClick={() => handleCheckpoint(step.title)}
                    disabled={index !== currentStepIndex}
                  >
                    チェック
                  </Button>
                )}
              </Box>
            )}
          </Paper>
        ))}

        {isFinished && (
          <Box mt={4}>
            <Typography variant="h6" color="primary">
              所要時間: {getElapsedTime()}
            </Typography>
          </Box>
        )}
      </Paper>

      {/* タイムライン */}
      <Paper elevation={2} sx={{ p: 4, borderRadius: 2 }}>
        <Typography variant="h6" gutterBottom>ステップ一覧</Typography>
        <Timeline position="left" sx={{ mt: 2 }}>
          {steps.map((step, index) => (
            <TimelineItem key={index}>
              <TimelineSeparator>
                <TimelineDot
                  sx={{
                    bgcolor: index < currentStepIndex ? 'primary.main' : 'grey.400',
                    width: 16,
                    height: 16
                  }}
                />
                {index < steps.length - 1 && <TimelineConnector sx={{ bgcolor: 'grey.400' }} />}
              </TimelineSeparator>
              <TimelineContent>
                <Typography variant="body1">{step.title}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {getCheckpointTime(step.title)}
                </Typography>
              </TimelineContent>
            </TimelineItem>
          ))}
        </Timeline>
      </Paper>
    </Container>
  );
};

export default TaskDetail;
